import {useMutation, useQueryClient} from '@tanstack/react-query'
import type {QueryKey, UseMutationOptions, UseMutationResult} from '@tanstack/react-query'
import {toast} from '@/hooks/use-toast'

export interface UseEntityMutationOptions<TData, TError, TVariables>
    extends UseMutationOptions<TData, TError, TVariables> {
    invalidate?: QueryKey | QueryKey[]
    successMessage?: string
    closeDialog?: () => void
}

/**
 * Wrapper around {@link useMutation} that folds the three patterns found
 * in virtually every detail page's onSuccess handler into declarative options:
 *
 *   - `invalidate`        — query key(s) to invalidate after a successful mutation
 *   - `successMessage`    — description for a success toast
 *   - `closeDialog`       — callback to close a modal/drawer
 *
 * Caller `onSuccess` / `onError` still fire; they run after the built-in
 * side effects on success and are untouched on error. On error, none of
 * the success side effects run — errors are surfaced via the global
 * MutationCache error handler (and any caller-provided `onError`).
 */
export function useEntityMutation<TData, TError = Error, TVariables = void>(
    options: UseEntityMutationOptions<TData, TError, TVariables>,
): UseMutationResult<TData, TError, TVariables> {
    const queryClient = useQueryClient()
    const {invalidate, successMessage, closeDialog, onSuccess, ...rest} = options

    return useMutation<TData, TError, TVariables>({
        ...rest,
        onSuccess: (data, variables, onMutateResult, context) => {
            if (invalidate) {
                const keys: QueryKey[] = isQueryKeyArray(invalidate) ? invalidate : [invalidate]
                for (const key of keys) {
                    queryClient.invalidateQueries({queryKey: key})
                }
            }
            if (successMessage) {
                toast({description: successMessage})
            }
            closeDialog?.()
            onSuccess?.(data, variables, onMutateResult, context)
        },
    })
}

function isQueryKeyArray(value: QueryKey | QueryKey[]): value is QueryKey[] {
    return value.length > 0 && Array.isArray(value[0])
}
